import { Fragment, FunctionComponent, useState, useCallback } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    useGetVoucherTemplatesQuery,
    useDeleteVoucherTemplateMutation,
    usePatchVoucherTemplateActiveMutation,
    usePostVoucherTemplateMutation,
    useLazyGetVoucherTemplateQuery,
    VoucherTemplateAdminDTO,
} from '@starjack/entities/voucherTemplate';
import { useNotification } from '@starjack/features/notifications';
import {
    PencilSquareIcon,
    TrashIcon,
    DocumentDuplicateIcon,
    PlusIcon,
} from '@heroicons/react/24/solid';
import { SvgIcon } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

type VoucherTemplateListContentProps = {
    liftOperatorId?: string;
};

export const VoucherTemplateListContent: FunctionComponent<
    VoucherTemplateListContentProps
> = ({ liftOperatorId }) => {
    const navigate = useNavigate();
    const { addNotification } = useNotification();

    const { data: templates = [], isLoading } = useGetVoucherTemplatesQuery(
        liftOperatorId ? { liftOperatorId } : undefined
    );
    const [deleteTemplate] = useDeleteVoucherTemplateMutation();
    const [patchActive] = usePatchVoucherTemplateActiveMutation();
    const [postTemplate] = usePostVoucherTemplateMutation();
    const [getTemplateDetail] = useLazyGetVoucherTemplateQuery();

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] =
        useState<VoucherTemplateAdminDTO | null>(null);

    const handleEdit = useCallback(
        (id: string) => {
            navigate(`/gutscheinvorlagen/bearbeiten?id=${id}`);
        },
        [navigate]
    );

    const handleCreate = useCallback(() => {
        navigate('/gutscheinvorlagen/bearbeiten');
    }, [navigate]);

    const handleToggleActive = useCallback(
        async (template: VoucherTemplateAdminDTO) => {
            try {
                await patchActive({
                    id: template.id,
                    body: { active: !template.active },
                }).unwrap();
                addNotification({
                    type: 'success',
                    message: template.active
                        ? `Vorlage "${template.name}" deaktiviert.`
                        : `Vorlage "${template.name}" aktiviert.`,
                });
            } catch (err) {
                // Surface the backend reason so the user knows why activation was
                // rejected (e.g. not all fields placed yet) instead of a generic error.
                const code = (
                    err as { data?: { errors?: { error?: string }[] } }
                )?.data?.errors?.[0]?.error;
                addNotification({
                    type: 'error',
                    message:
                        code === 'template_incomplete'
                            ? 'Vorlage unvollständig: Bitte platzieren Sie alle Felder (Text, Betrag, Code, Absender, Empfänger), bevor Sie die Vorlage aktivieren.'
                            : 'Fehler beim Ändern des Status.',
                });
            }
        },
        [patchActive, addNotification]
    );

    const handleDeleteConfirm = useCallback(async () => {
        if (!templateToDelete) return;
        try {
            await deleteTemplate(templateToDelete.id).unwrap();
            addNotification({
                type: 'success',
                message: `Vorlage "${templateToDelete.name}" gelöscht.`,
            });
        } catch {
            addNotification({
                type: 'error',
                message:
                    'Vorlage kann nicht gelöscht werden, da sie von bestehenden Gutscheinen verwendet wird.',
            });
        }
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
    }, [templateToDelete, deleteTemplate, addNotification]);

    const handleDuplicate = useCallback(
        async (template: VoucherTemplateAdminDTO) => {
            try {
                const detail = await getTemplateDetail(template.id).unwrap();

                const result = await postTemplate({
                    liftOperatorId: template.liftOperatorId,
                    language: template.language,
                    name: `${template.name} (Kopie)`,
                    defaultText: template.defaultText,
                    elements: detail.elements,
                }).unwrap();
                addNotification({
                    type: 'success',
                    message: `Vorlage "${template.name}" dupliziert. Bitte laden Sie das Hintergrund-PDF erneut hoch.`,
                });
                navigate(`/gutscheinvorlagen/bearbeiten?id=${result.id}`);
            } catch {
                addNotification({
                    type: 'error',
                    message: 'Fehler beim Duplizieren.',
                });
            }
        },
        [getTemplateDetail, postTemplate, addNotification, navigate]
    );

    const columns: GridColDef<VoucherTemplateAdminDTO>[] = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 2,
            minWidth: 200,
        },
        {
            field: 'language',
            headerName: 'Sprache',
            width: 100,
        },
        {
            field: 'active',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => (
                <Chip
                    label={params.value ? 'Aktiv' : 'Entwurf'}
                    color={params.value ? 'success' : 'default'}
                    size='small'
                    onClick={() => handleToggleActive(params.row)}
                    sx={{ cursor: 'pointer' }}
                />
            ),
        },
        {
            field: 'source',
            headerName: 'Quelle',
            width: 120,
        },
        {
            field: 'actions',
            headerName: 'Aktionen',
            width: 160,
            sortable: false,
            renderCell: (params) => (
                <Stack direction='row' spacing={0.5}>
                    <Tooltip title='Bearbeiten'>
                        <IconButton
                            size='small'
                            onClick={() => handleEdit(params.row.id)}
                        >
                            <SvgIcon fontSize='small'>
                                <PencilSquareIcon />
                            </SvgIcon>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Duplizieren'>
                        <IconButton
                            size='small'
                            onClick={() => handleDuplicate(params.row)}
                        >
                            <SvgIcon fontSize='small'>
                                <DocumentDuplicateIcon />
                            </SvgIcon>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Löschen'>
                        <IconButton
                            size='small'
                            onClick={() => {
                                setTemplateToDelete(params.row);
                                setDeleteDialogOpen(true);
                            }}
                        >
                            <SvgIcon fontSize='small'>
                                <TrashIcon />
                            </SvgIcon>
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
        },
    ];

    return (
        <Fragment>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                }}
            >
                <Typography variant='h4'>Gutscheinvorlagen</Typography>
                <Button
                    variant='contained'
                    startIcon={
                        <SvgIcon fontSize='small'>
                            <PlusIcon />
                        </SvgIcon>
                    }
                    onClick={handleCreate}
                >
                    Neue Vorlage
                </Button>
            </Box>

            <Card>
                <CardContent>
                    <DataGrid
                        rows={templates}
                        columns={columns}
                        loading={isLoading}
                        autoHeight
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 10 },
                            },
                        }}
                        slots={{
                            noRowsOverlay: () => (
                                <Stack
                                    alignItems='center'
                                    justifyContent='center'
                                    sx={{ height: '100%', minHeight: 200 }}
                                >
                                    <Typography
                                        variant='body1'
                                        color='text.secondary'
                                        sx={{ mb: 2 }}
                                    >
                                        Noch keine Gutscheinvorlagen vorhanden.
                                    </Typography>
                                    <Button
                                        variant='contained'
                                        size='small'
                                        onClick={handleCreate}
                                    >
                                        Neue Vorlage erstellen
                                    </Button>
                                </Stack>
                            ),
                        }}
                        sx={{
                            border: 'none',
                            // With autoHeight the grid sizes the "no rows" overlay from this
                            // variable, not from the overlay's own content, so the empty state
                            // gets clipped unless the reserved height covers it.
                            '--DataGrid-overlayHeight': '200px',
                            '& .MuiDataGrid-cell:focus': {
                                outline: 'none',
                            },
                        }}
                    />
                </CardContent>
            </Card>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Vorlage löschen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Vorlage &quot;{templateToDelete?.name}&quot; wirklich
                        löschen? Diese Aktion kann nicht rückgängig gemacht
                        werden.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color='error'
                        variant='contained'
                    >
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};
